import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, Plus, Download, Eye, Filter, Search, Trash2, ChevronDown, X, Mail, Loader2, ChevronLeft, ChevronRight, Pencil, FileText, Copy, CheckCircle, Send } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import { kvaApi } from '../api/kvaApi';
import { getAllKunden } from '../api/kundenApi';
import { rechnungApi } from '../api/rechnungApi';
import { useKvaRefresh } from '../hooks/useDataRefresh';

function Kostenvoranschlaege() {
  const navigate = useNavigate();
  const [kvas, setKvas] = useState([]);
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter
  const [filterKunde, setFilterKunde] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterNummer, setFilterNummer] = useState('');
  const [filterTyp, setFilterTyp] = useState('');

  // Kundensuche State
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const kundenDropdownRef = useRef(null);

  // Versand State
  const [sendingId, setSendingId] = useState(null);
  const [convertingId, setConvertingId] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Initial load
  useEffect(() => {
    loadData();
  }, []);

  // Filter-Wechsel - silent reload um Flackern zu vermeiden
  useEffect(() => {
    if (kvas.length > 0 || kunden.length > 0) {
      loadData(true);
    }
  }, [filterKunde, filterStatus]);

  // Schließe Dropdown wenn außerhalb geklickt wird
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(event.target)) {
        setShowKundenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const params = {};
      if (filterKunde) params.kundeId = filterKunde;
      if (filterStatus) params.status = filterStatus;

      const [kvasData, kundenData] = await Promise.all([
        kvaApi.getKvas(params),
        getAllKunden()
      ]);

      setKvas(kvasData);
      setKunden(kundenData);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Echtzeit-Updates: Reagiere auf KI-Assistent Aktionen
  const handleKvaRefresh = useCallback(() => {
    loadData(true); // Silent reload ohne Loading-Spinner
  }, [filterKunde, filterStatus]);

  useKvaRefresh(handleKvaRefresh);

  const handleDelete = async (kva) => {
    if (!confirm(`Möchten Sie den Kostenvoranschlag ${kva.nummer} wirklich löschen?`)) {
      return;
    }

    try {
      await kvaApi.deleteKva(kva.id);
      loadData();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert(error.response?.data?.error || 'Fehler beim Löschen des Kostenvoranschlags');
    }
  };

  const handleDuplicate = async (kva) => {
    try {
      const fullKva = await kvaApi.getKva(kva.id);
      const duplicateData = {
        kundeId: fullKva.kundeId,
        anfrageId: fullKva.anfrageId,
        einleitung: fullKva.einleitung || '',
        schlusstext: fullKva.schlusstext || '',
        betreff: fullKva.betreff || '',
        kundenReferenz: fullKva.kundenReferenz || '',
        gesamtRabattProzent: fullKva.gesamtRabattProzent || 0,
        dokumentTyp: fullKva.dokumentTyp || 'KOSTENVORANSCHLAG',
        verbindlich: fullKva.verbindlich || false,
        toleranzProzent: fullKva.toleranzProzent ?? 20,
        positionen: (fullKva.positionen || []).map(pos => ({
          beschreibung: pos.beschreibung,
          menge: pos.menge,
          einheit: pos.einheit,
          einzelpreis: pos.einzelpreis / 100, // Cent → Euro
          ustSatz: pos.ustSatz,
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          berechnungsSchritte: pos.berechnungsSchritte
            ? pos.berechnungsSchritte.map(s => ({
                ...s,
                einzelpreis: s.einzelpreis / 100,
                gesamt: s.gesamt / 100
              }))
            : []
        }))
      };
      navigate('/kostenvoranschlaege/neu', { state: { duplicate: duplicateData } });
    } catch (error) {
      console.error('Fehler beim Duplizieren:', error);
      alert('Fehler beim Duplizieren des Kostenvoranschlags');
    }
  };

  const handleConvertToRechnung = async (kva) => {
    const typText = kva.dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'KVA';
    if (!confirm(`${typText} ${kva.nummer} in eine Rechnung umwandeln?`)) return;

    try {
      setConvertingId(kva.id);
      const conversionData = await kvaApi.convertToRechnung(kva.id);
      // Navigate to Rechnung creation with pre-filled data
      navigate('/rechnungen/neu', { state: { fromKva: conversionData } });
    } catch (error) {
      console.error('Fehler bei Konvertierung:', error);
      alert(error.response?.data?.error || 'Fehler bei der Konvertierung');
    } finally {
      setConvertingId(null);
    }
  };

  const handleSend = async (kva) => {
    if (!kva.kunde.email) {
      alert('Kunde hat keine E-Mail-Adresse hinterlegt');
      return;
    }

    if (!confirm(`Kostenvoranschlag ${kva.nummer} an ${kva.kunde.email} senden?`)) {
      return;
    }

    try {
      setSendingId(kva.id);
      const result = await kvaApi.sendKva(kva.id);
      alert(result.message);
      loadData();
    } catch (error) {
      console.error('Fehler beim Versenden:', error);
      alert(error.response?.data?.error || 'Fehler beim Versenden des Kostenvoranschlags');
    } finally {
      setSendingId(null);
    }
  };

  // --- Auftragsbestätigung (AB) Handlers ---
  const [creatingAbId, setCreatingAbId] = useState(null);
  const [sendingAbId, setSendingAbId] = useState(null);

  const handleCreateAB = async (kva) => {
    const typText = kva.dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'KVA';
    if (!confirm(`Auftragsbestätigung für ${typText} ${kva.nummer} erstellen?`)) return;

    try {
      setCreatingAbId(kva.id);
      await kvaApi.createAB(kva.id);
      alert('Auftragsbestätigung wurde erstellt.');
      loadData();
    } catch (error) {
      console.error('Fehler bei AB-Erstellung:', error);
      alert(error.response?.data?.error || 'Fehler bei der AB-Erstellung');
    } finally {
      setCreatingAbId(null);
    }
  };

  const handleSendAB = async (kva) => {
    if (!kva.kunde?.email) {
      alert('Kunde hat keine E-Mail-Adresse hinterlegt');
      return;
    }
    if (!confirm(`Auftragsbestätigung ${kva.abNummer} an ${kva.kunde.email} senden?`)) return;

    try {
      setSendingAbId(kva.id);
      const result = await kvaApi.sendAB(kva.id);
      alert(result.message);
      loadData();
    } catch (error) {
      console.error('Fehler beim AB-Versand:', error);
      alert(error.response?.data?.error || 'Fehler beim Versand der Auftragsbestätigung');
    } finally {
      setSendingAbId(null);
    }
  };

  const handleConvertAbToRechnung = async (kva) => {
    if (!confirm(`Auftragsbestätigung ${kva.abNummer} in eine Rechnung umwandeln?`)) return;

    try {
      setConvertingId(kva.id);
      const conversionData = await kvaApi.convertAbToRechnung(kva.id);
      navigate('/rechnungen/neu', { state: { fromKva: conversionData } });
    } catch (error) {
      console.error('Fehler bei AB→Rechnung:', error);
      alert(error.response?.data?.error || 'Fehler bei der Konvertierung');
    } finally {
      setConvertingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'entwurf': 'bg-gray-100 text-gray-700',
      'versendet': 'bg-blue-100 text-blue-700',
      'angenommen': 'bg-green-100 text-green-700',
      'ab_erstellt': 'bg-emerald-100 text-emerald-700',
      'abgelehnt': 'bg-red-100 text-red-700',
      'storniert': 'bg-red-100 text-red-700',
      'in_arbeit': 'bg-purple-100 text-purple-700'
    };

    const labels = {
      'entwurf': 'Entwurf',
      'versendet': 'Versendet',
      'angenommen': 'Angenommen',
      'ab_erstellt': 'AB erstellt',
      'abgelehnt': 'Abgelehnt',
      'storniert': 'Storniert',
      'in_arbeit': 'In Arbeit'
    };

    // Fallback: unbekannter Status wird als neutrales Badge angezeigt, statt
    // leer. So fällt ein neuer DB-Status-Wert sofort visuell auf, ohne
    // dass die Spalte einfach leer bleibt.
    const badgeClass = badges[status] || 'bg-gray-100 text-gray-600';
    const label = labels[status] || status || '—';

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
        {label}
      </span>
    );
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Gefilterte KVAs basierend auf Nummer-Suche und Typ
  const gefilterteKvas = kvas.filter(kva => {
    if (filterNummer && !kva.nummer.toLowerCase().includes(filterNummer.toLowerCase())) return false;
    if (filterTyp && kva.dokumentTyp !== filterTyp) return false;
    return true;
  });

  // Gefilterte Kundenliste basierend auf Suche
  const gefilterteKunden = kunden.filter(kunde => {
    const suchText = kundenSuche.toLowerCase();
    return (
      kunde.name.toLowerCase().includes(suchText) ||
      kunde.email?.toLowerCase().includes(suchText) ||
      kunde.telefon?.toLowerCase().includes(suchText)
    );
  });

  // Kunde auswählen
  const handleKundeSelect = (kunde) => {
    setSelectedKunde(kunde);
    setFilterKunde(kunde.id);
    setKundenSuche('');
    setShowKundenDropdown(false);
  };

  // Kundenauswahl entfernen
  const handleKundeRemove = () => {
    setSelectedKunde(null);
    setFilterKunde('');
    setKundenSuche('');
  };

  // Prüfen ob Filter aktiv sind
  const hasActiveFilters = filterKunde || filterStatus || filterNummer || filterTyp;

  // Alle Filter zurücksetzen
  const resetFilters = () => {
    setFilterKunde('');
    setFilterStatus('');
    setFilterNummer('');
    setFilterTyp('');
    setSelectedKunde(null);
    setKundenSuche('');
    setCurrentPage(1);
  };

  // Pagination Berechnung
  const totalPages = Math.ceil(gefilterteKvas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedKvas = gefilterteKvas.slice(startIndex, endIndex);

  // Reset auf Seite 1 wenn Filter sich ändern
  useEffect(() => {
    setCurrentPage(1);
  }, [filterKunde, filterStatus, filterNummer]);

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold flex items-center gap-3">
              <ClipboardList className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
              Kostenvoranschläge
            </h1>
            <p className="text-sm sm:text-base text-text-muted mt-1">
              {gefilterteKvas.length} Kostenvoranschläge
            </p>
            {/* Mobile: Button unter Titel */}
            <button
              onClick={() => navigate('/kostenvoranschlaege/neu')}
              className="flex sm:hidden items-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition mt-3"
            >
              <Plus className="w-5 h-5" />
              Neuer KVA
            </button>
          </div>

          {/* Desktop: Button rechts */}
          <button
            onClick={() => navigate('/kostenvoranschlaege/neu')}
            className="hidden sm:flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition"
          >
            <Plus className="w-5 h-5" />
            Neuer KVA
          </button>
        </div>

        {/* Filter */}
        <div className="bg-surface rounded-card shadow-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-text-muted" />
              <h2 className="text-lg font-semibold">Filter</h2>
            </div>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-primary hover:text-primary-dark font-medium text-sm transition"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* KVA-Nummer Suche */}
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                KVA-Nummer
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filterNummer}
                  onChange={(e) => setFilterNummer(e.target.value)}
                  placeholder="z.B. KVA-2025-00001"
                  className="w-full pl-10 pr-3 py-2 border border-border rounded-button bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Kunde Filter mit Suchfunktion */}
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Kunde
              </label>
              <div className="relative" ref={kundenDropdownRef}>
                {selectedKunde ? (
                  <div className="flex items-center justify-between w-full px-3 py-2 border border-border rounded-button bg-gray-50">
                    <span className="font-medium truncate">{selectedKunde.name}</span>
                    <button
                      type="button"
                      onClick={handleKundeRemove}
                      className="p-1 hover:bg-gray-200 rounded transition ml-2"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={kundenSuche}
                      onChange={(e) => {
                        setKundenSuche(e.target.value);
                        setShowKundenDropdown(true);
                      }}
                      onFocus={() => setShowKundenDropdown(true)}
                      placeholder="Kunde suchen..."
                      className="w-full pl-10 pr-10 py-2 border border-border rounded-button bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKundenDropdown(!showKundenDropdown)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showKundenDropdown ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                )}

                {showKundenDropdown && !selectedKunde && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {gefilterteKunden.length === 0 ? (
                      <div className="px-4 py-3 text-text-muted text-center">
                        {kundenSuche ? 'Kein Kunde gefunden' : 'Keine Kunden vorhanden'}
                      </div>
                    ) : (
                      gefilterteKunden.map(kunde => (
                        <button
                          key={kunde.id}
                          type="button"
                          onClick={() => handleKundeSelect(kunde)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                        >
                          <div className="font-medium">{kunde.name}</div>
                          <div className="text-sm text-text-muted flex gap-3">
                            {kunde.email && <span>{kunde.email}</span>}
                            {kunde.telefon && <span>{kunde.telefon}</span>}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-button bg-surface"
              >
                <option value="">Alle Status</option>
                <option value="entwurf">Entwurf</option>
                <option value="versendet">Versendet</option>
                <option value="angenommen">Angenommen</option>
                <option value="ab_erstellt">AB erstellt</option>
              </select>
            </div>

            {/* Dokumenttyp Filter */}
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Typ
              </label>
              <select
                value={filterTyp}
                onChange={(e) => setFilterTyp(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-button bg-surface"
              >
                <option value="">Alle Typen</option>
                <option value="KOSTENVORANSCHLAG">Kostenvoranschläge</option>
                <option value="ANGEBOT">Angebote</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summen Übersicht */}
        {gefilterteKvas.length > 0 && (
          <div className="bg-surface rounded-card shadow-card p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Übersicht</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-text-muted">Gesamt</p>
                <p className="text-xl font-bold">
                  {formatCurrency(
                    gefilterteKvas.reduce((sum, k) => sum + k.bruttogesamt, 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Entwürfe</p>
                <p className="text-xl font-bold text-gray-600">
                  {formatCurrency(
                    gefilterteKvas
                      .filter(k => k.status === 'entwurf')
                      .reduce((sum, k) => sum + k.bruttogesamt, 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Versendet</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(
                    gefilterteKvas
                      .filter(k => k.status === 'versendet')
                      .reduce((sum, k) => sum + k.bruttogesamt, 0)
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Kostenvoranschläge Liste */}
        <div className="bg-surface rounded-card shadow-card overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : gefilterteKvas.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-text-muted">Keine Kostenvoranschläge gefunden</p>
              <button
                onClick={() => navigate('/kostenvoranschlaege/neu')}
                className="mt-4 text-primary hover:underline"
              >
                Ersten Kostenvoranschlag erstellen
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Nr.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Typ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Kunde
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase hidden md:table-cell">
                      Anfrage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase hidden lg:table-cell">
                      Datum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase hidden lg:table-cell">
                      Gültig bis
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                      Betrag
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedKvas.map(kva => (
                    <tr key={kva.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium">
                          {kva.nummer}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            kva.dokumentTyp === 'ANGEBOT'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {kva.dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'KVA'}
                          </span>
                          <span className="text-xs text-text-muted mt-1">
                            {kva.verbindlich ? 'verbindl.' : 'unverbindl.'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {kva.kunde ? (
                          <Link
                            to={`/kunden/${kva.kunde.id}`}
                            className="text-primary hover:underline"
                          >
                            {kva.kunde.name}
                          </Link>
                        ) : (
                          <span className="text-text-muted">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm hidden md:table-cell">
                        {kva.anfrage?.betreff || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-text-muted hidden lg:table-cell">
                        {formatDate(kva.datum)}
                      </td>
                      <td className="px-6 py-4 text-sm text-text-muted hidden lg:table-cell">
                        {formatDate(kva.gueltigBis)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium">
                        {formatCurrency(kva.bruttogesamt)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(kva.status)}
                        {kva.versendetAm && (
                          <div className="text-xs text-text-muted mt-1">
                            {formatDate(kva.versendetAm)}
                          </div>
                        )}
                        {kva.abNummer && (
                          <div className="text-xs text-emerald-600 font-medium mt-1">
                            {kva.abNummer}
                            {kva.abVersendetAm && <span className="text-text-muted ml-1">({formatDate(kva.abVersendetAm)})</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {kva.pdfPfad && (
                            <>
                              <a
                                href={kvaApi.getPdfUrl(kva)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-primary hover:bg-primary-light rounded-button transition"
                                title="PDF öffnen"
                              >
                                <Eye className="w-4 h-4" />
                              </a>

                              <a
                                href={kvaApi.getPdfUrl(kva)}
                                download={`KVA_${kva.nummer}.pdf`}
                                className="p-2 text-text-muted hover:bg-gray-100 rounded-button transition"
                                title="PDF herunterladen"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            </>
                          )}

                          {/* Bearbeiten - nur für Entwürfe */}
                          {kva.status === 'entwurf' && (
                            <button
                              onClick={() => navigate(`/kostenvoranschlaege/${kva.id}/bearbeiten`)}
                              className="p-2 text-amber-600 hover:bg-amber-50 rounded-button transition"
                              title="Entwurf bearbeiten"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}

                          {/* E-Mail versenden - nur für Entwürfe */}
                          {kva.status === 'entwurf' && kva.pdfPfad && (
                            <button
                              onClick={() => handleSend(kva)}
                              disabled={sendingId === kva.id}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-button transition disabled:opacity-50"
                              title={kva.kunde.email ? `An ${kva.kunde.email} senden` : 'Keine E-Mail-Adresse'}
                            >
                              {sendingId === kva.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                            </button>
                          )}

                          {/* Duplizieren */}
                          <button
                            onClick={() => handleDuplicate(kva)}
                            className="p-2 text-violet-600 hover:bg-violet-50 rounded-button transition"
                            title="Duplizieren"
                          >
                            <Copy className="w-4 h-4" />
                          </button>

                          {/* AB erstellen (nur wenn versendet/angenommen und noch keine AB) */}
                          {['versendet', 'angenommen'].includes(kva.status) && !kva.abNummer && (
                            <button
                              onClick={() => handleCreateAB(kva)}
                              disabled={creatingAbId === kva.id}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-button transition disabled:opacity-50"
                              title="Auftragsbestätigung erstellen"
                            >
                              {creatingAbId === kva.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                            </button>
                          )}

                          {/* AB versenden (wenn AB erstellt, aber noch nicht versendet) */}
                          {kva.abNummer && !kva.abVersendetAm && (
                            <button
                              onClick={() => handleSendAB(kva)}
                              disabled={sendingAbId === kva.id}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-button transition disabled:opacity-50"
                              title={`AB ${kva.abNummer} versenden`}
                            >
                              {sendingAbId === kva.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </button>
                          )}

                          {/* AB-PDF ansehen */}
                          {kva.abPdfPfad && (
                            <a
                              href={kvaApi.getAbPdfUrl(kva)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-button transition inline-flex"
                              title={`AB ${kva.abNummer} PDF`}
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                          )}

                          {/* KVA/AB → Rechnung */}
                          <button
                            onClick={() => kva.abNummer ? handleConvertAbToRechnung(kva) : handleConvertToRechnung(kva)}
                            disabled={convertingId === kva.id}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-button transition disabled:opacity-50"
                            title={kva.abNummer ? `AB ${kva.abNummer} → Rechnung` : `${kva.dokumentTyp === 'ANGEBOT' ? 'Angebot' : 'KVA'} → Rechnung`}
                          >
                            {convertingId === kva.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4" />
                            )}
                          </button>

                          {kva.status === 'entwurf' && (
                            <button
                              onClick={() => handleDelete(kva)}
                              className="p-2 text-danger hover:bg-red-50 rounded-button transition"
                              title="Entwurf löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-text-muted">
                Zeige {startIndex + 1} - {Math.min(endIndex, gefilterteKvas.length)} von {gefilterteKvas.length} Kostenvoranschlägen
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Zurück
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Zeige erste, letzte, aktuelle und benachbarte Seiten
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, idx, arr) => (
                      <span key={page} className="flex items-center">
                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                          <span className="px-2 text-text-muted">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 text-sm rounded-button transition ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'hover:bg-gray-50 border border-border'
                          }`}
                        >
                          {page}
                        </button>
                      </span>
                    ))}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Kostenvoranschlaege;
